/**
 * Validation Helper Functions
 * Utility functions for form validation and data validation
 */

import { PASSWORD_RULES, USER_ROLES } from "../constants/userConstants";

/**
 * Validates password strength
 * @param {string} password - Password to validate
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validatePassword = (password) => {
  const errors = [];

  if (!password) {
    errors.push("Password is required");
    return { isValid: false, errors };
  }

  if (password.length < PASSWORD_RULES.MIN_LENGTH) {
    errors.push(
      `Password must be at least ${PASSWORD_RULES.MIN_LENGTH} characters`
    );
  }

  if (!PASSWORD_RULES.REGEX.test(password)) {
    errors.push("Password must contain uppercase, lowercase and number");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  if (!email) {
    return { isValid: false, error: "Email is required" };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid = emailRegex.test(email);

  return {
    isValid,
    error: isValid ? "" : "Please enter a valid email address",
  };
};

/**
 * Validates user name
 * @param {string} name - Name to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateName = (name) => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: "Name is required" };
  }

  if (name.length < 2) {
    return { isValid: false, error: "Name must be at least 2 characters" };
  }

  if (name.length > 100) {
    return { isValid: false, error: "Name must be less than 100 characters" };
  }

  return { isValid: true, error: "" };
};

/**
 * Validates user role (now validates role_id)
 * @param {number} role_id - Role ID to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateRole = (role_id) => {
  if (!role_id) {
    return { isValid: false, error: "Role is required" };
  }

  // Validate role_id is a number
  if (typeof role_id !== "number" || role_id < 1) {
    return { isValid: false, error: "Please select a valid role" };
  }

  return { isValid: true, error: "" };
};

/**
 * Validates company name
 * @param {string} company - Company name to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateCompany = (company) => {
  if (!company || company.trim().length === 0) {
    return { isValid: false, error: "Company is required" };
  }

  if (company.length < 2) {
    return {
      isValid: false,
      error: "Company name must be at least 2 characters",
    };
  }

  if (company.length > 200) {
    return {
      isValid: false,
      error: "Company name must be less than 200 characters",
    };
  }

  return { isValid: true, error: "" };
};

/**
 * Validates password confirmation matches
 * @param {string} password - Original password
 * @param {string} confirmPassword - Confirmation password
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validatePasswordMatch = (password, confirmPassword) => {
  if (!confirmPassword) {
    return { isValid: false, error: "Please confirm the password" };
  }

  if (password !== confirmPassword) {
    return { isValid: false, error: "Passwords do not match" };
  }

  return { isValid: true, error: "" };
};

/**
 * Validates entire user form
 * @param {Object} formData - Form data object
 * @param {boolean} isEdit - Whether this is an edit (password optional) or create (password required)
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateUserForm = (formData, isEdit = false) => {
  const errors = {};

  // Validate name
  const nameValidation = validateName(formData.name);
  if (!nameValidation.isValid) {
    errors.name = nameValidation.error;
  }

  // Validate email
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }

  // Validate company
  const companyValidation = validateCompany(formData.company);
  if (!companyValidation.isValid) {
    errors.company = companyValidation.error;
  }

  // Validate role_id (changed from role to role_id)
  const roleValidation = validateRole(formData.role_id);
  if (!roleValidation.isValid) {
    errors.role_id = roleValidation.error;
  }

  // Validate password (only required for new users)
  if (!isEdit && formData.password) {
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.errors.join(", ");
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Sanitizes user input (removes extra spaces, trims)
 * @param {string} input - Input string to sanitize
 * @returns {string} Sanitized string
 */
export const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;

  return input.trim().replace(/\s+/g, " "); // Replace multiple spaces with single space
};

/**
 * Sanitizes user form data
 * @param {Object} formData - Form data object
 * @returns {Object} Sanitized form data
 */
export const sanitizeUserFormData = (formData) => {
  return {
    ...formData,
    name: sanitizeInput(formData.name),
    email: sanitizeInput(formData.email)?.toLowerCase(),
    company: sanitizeInput(formData.company),
  };
};

/**
 * Gets Ant Design form validation rules for password
 * @returns {Array} Array of validation rule objects
 */
export const getPasswordValidationRules = () => [
  {
    required: true,
    message: "Please enter a new password",
  },
  {
    min: PASSWORD_RULES.MIN_LENGTH,
    message: `Password must be at least ${PASSWORD_RULES.MIN_LENGTH} characters`,
  },
  {
    pattern: PASSWORD_RULES.REGEX,
    message: "Password must contain uppercase, lowercase and number",
  },
];

/**
 * Gets Ant Design form validation rules for password confirmation
 * @param {Function} getFieldValue - Function to get form field value
 * @returns {Array} Array of validation rule objects
 */
export const getPasswordConfirmationRules = (getFieldValue) => [
  {
    required: true,
    message: "Please confirm the password",
  },
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue("newPassword") === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Passwords do not match"));
    },
  }),
];

/**
 * Gets Ant Design form validation rules for email
 * @returns {Array} Array of validation rule objects
 */
export const getEmailValidationRules = () => [
  {
    required: true,
    message: "Please enter email address",
  },
  {
    type: "email",
    message: "Please enter a valid email address",
  },
];

/**
 * Gets Ant Design form validation rules for name
 * @returns {Array} Array of validation rule objects
 */
export const getNameValidationRules = () => [
  {
    required: true,
    message: "Please enter name",
  },
  {
    min: 2,
    message: "Name must be at least 2 characters",
  },
  {
    max: 100,
    message: "Name must be less than 100 characters",
  },
];
